import { useEffect, useMemo, useState } from 'react';
import { Dayjs } from 'dayjs';
import useCurrentListExport from '@/hooks/useCurrentListExport';
import useExportDelivery from '@/hooks/useExportDelivery';
import useCustomerIO from '@/hooks/useCustomerIO';
import { useOrderExportCount } from '@/hooksApi/useOrderExportCount';
import {
  selectCloseExport,
  selectExportSelection,
  selectIsExportOpen,
  useExportModalStore,
} from '@/stores/useExportModalStore';
import {
  ExportDatePreset,
  ExportScope,
  buildExportQuery,
  exportScopeSlug,
  presetRange,
  validateExportRange,
} from '@/utils/helpers/orderExport';
import { groupStatusesByMerchantTitle } from '@/utils/helpers/orderStatusHelpers';

/** Status groups as the merchant sees them: group title → order status slugs. */
export const EXPORT_STATUS_GROUPS = groupStatusesByMerchantTitle();

const DEFAULT_PRESET: ExportDatePreset = 'last_30_days';

/**
 * BOXY-409 — state of the one orders export modal: which scope, its live
 * count, the custom filters and their validation, and the submit.
 */
const useOrdersExport = () => {
  const isOpen = useExportModalStore(selectIsExportOpen);
  const selection = useExportModalStore(selectExportSelection);
  const closeExport = useExportModalStore(selectCloseExport);
  const currentList = useCurrentListExport();
  const { deliver, isExporting } = useExportDelivery();
  const { trackEvent } = useCustomerIO();

  const hasSelection = selection.isAllSelected || selection.uids.length > 0;
  const [scope, setScopeState] = useState<ExportScope>('tab');
  // Set once the merchant picks a card; until then the scope follows the selection.
  const [scopeChosen, setScopeChosen] = useState(false);
  const setScope = (next: ExportScope) => {
    setScopeChosen(true);
    setScopeState(next);
  };
  const [statusGroups, setStatusGroups] = useState<string[]>([]);
  const [preset, setPreset] = useState<ExportDatePreset>(DEFAULT_PRESET);
  const [range, setRange] = useState<[Dayjs?, Dayjs?]>(presetRange(DEFAULT_PRESET) ?? []);

  // Each opening starts fresh, on "Selected" when rows are ticked.
  useEffect(() => {
    if (!isOpen) return;
    setScopeChosen(false);
    setScopeState(hasSelection ? 'selected' : 'tab');
    setStatusGroups([]);
    setPreset(DEFAULT_PRESET);
    setRange(presetRange(DEFAULT_PRESET) ?? []);
    trackEvent('export_opened', { has_selection: hasSelection });
  }, [isOpen]);

  // The page mirrors its selection after render, so it can land after the
  // modal opened (e.g. from the mixed-status chooser): follow it until a pick.
  useEffect(() => {
    if (isOpen && hasSelection && !scopeChosen) setScopeState('selected');
  }, [isOpen, hasSelection, scopeChosen]);

  const [from, to] = range;
  // The period applies to every scope but ticked uids (the merchant named the
  // rows). Select-all is the tab's filters, so it takes the period too.
  const showPeriod = scope !== 'selected' || selection.isAllSelected;
  const periodError = validateExportRange(from, to);
  const dateError = showPeriod ? periodError : null;

  const queryFor = (target: ExportScope) =>
    buildExportQuery({
      scope: target,
      selection,
      tabQuery: currentList.query,
      statuses: statusGroups.flatMap((group) => EXPORT_STATUS_GROUPS[group] ?? []),
      from,
      to,
    });

  const countable = isOpen && !periodError;
  const tab = useOrderExportCount(countable ? queryFor('tab') : null);
  const all = useOrderExportCount(countable ? queryFor('all') : null);
  const custom = useOrderExportCount(countable && scope === 'custom' ? queryFor('custom') : null);
  // Select-all counts its own export query, so the number shown is the rows exported.
  const allSelected = useOrderExportCount(
    countable && selection.isAllSelected ? queryFor('selected') : null,
  );

  const counts: Record<ExportScope, number | undefined> = {
    selected: selection.isAllSelected ? allSelected.count : selection.uids.length,
    tab: tab.count,
    all: all.count,
    custom: custom.count,
  };
  const isCounting = {
    selected: selection.isAllSelected && allSelected.isCounting,
    tab: tab.isCounting,
    all: all.isCounting,
    custom: custom.isCounting,
  }[scope];
  const count = counts[scope];
  const isEmpty = !isCounting && count === 0;
  const canExport = !dateError && !isCounting && !isExporting && !!count;

  const changePreset = (next: ExportDatePreset) => {
    setPreset(next);
    const nextRange = presetRange(next);
    if (nextRange) setRange(nextRange);
  };

  const changeRange = (nextFrom?: Dayjs, nextTo?: Dayjs) => {
    setPreset('custom');
    setRange([nextFrom, nextTo]);
  };

  const submit = async () => {
    if (!canExport || count === undefined) return;
    const done = await deliver({
      scope,
      count,
      query: queryFor(scope),
      slug: exportScopeSlug(scope, currentList.slug),
    });
    if (done) closeExport();
  };

  const statusOptions = useMemo(
    () =>
      Object.entries(EXPORT_STATUS_GROUPS).map(([group, slugs]) => ({
        value: group,
        firstSlug: slugs[0],
      })),
    [],
  );

  return {
    isOpen,
    close: closeExport,
    hasSelection,
    showPeriod,
    selection,
    tabLabel: currentList.label,
    scope,
    setScope,
    counts,
    count,
    isCounting,
    isEmpty,
    canExport,
    isExporting,
    submit,
    statusOptions,
    statusGroups,
    setStatusGroups,
    preset,
    changePreset,
    from,
    to,
    changeRange,
    dateError,
  };
};

export default useOrdersExport;
